import React from 'react'
// @src imports
import { mapNullEntries } from 'src/helpers'
import {
  useAccount,
  useActions,
  useCallback,
  useMutations,
  useQueryParams,
  useState,
  useUpdateAccount,
  useVertical,
} from 'src/hooks'
import { Alert, ProfileAvatar, UiCard } from 'src/chrome'
import { SubscriptionType } from 'src/user/fragments'
import {
  GS_AFFILIATE_ID,
  SENDOGO_RP_STRIPE_ID,
} from 'src/marketing/plans/constants'
import {
  AccountSettingsInput,
  AddressFragment,
  Label,
  OverridableUserLabel,
  Storefront,
  UserLabelOverrideAction,
  UserSettingsFragment,
} from 'src/graphql'
import {
  EmailPreferences,
  PersonalSiteOptions,
  ShippingAddress,
  SponsorCard,
  Subscriptions,
  UserProfileForm,
} from 'src/dashboard/components'

import styles from '../styles/account.module.scss'
import {
  Button,
  ConfirmDialog,
  Div,
  Sheet,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { getAccount } from 'src/redux/selectors/user'
import SharingInformationCard from '../components/SharingInformationCard'
import StreamLinksCard from '../components/StreamLinksCard'
import VerticalTabs from '../components/VerticalTabs'
import { ButtonColorChoices } from 'src/theme/buttonColors'
import ProgramOptIn, {
  ActionKeys,
} from 'src/onboarding/components/ProgramOptIn'
import { FlexRow } from 'src/styled'
import { LEGACY_SITE_URL } from 'src/app/api'

const emptyShippingAddress: AddressFragment = {
  firstName: '',
  lastName: '',
  company: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  country: 'United States',
  postalCode: '',
}

const Account: React.FC = () => {
  const actions = useActions()
  const vertical = useVertical()
  const queryParams = useQueryParams()
  const isClientSOC = vertical.id === 'soc'

  const mutations = useMutations()

  const account = useAccount()
  const accountWithAddons = getAccount(account)
  const updateAccount = useUpdateAccount()

  const initialUser = {
    shippingAddress: emptyShippingAddress,
    firstName: account.firstName,
    lastName: account.lastName,
    username: account.username,
    email: account.email,
    profileImageUrl: account.profileImageUrl ?? undefined,
    ...(account.shippingAddress && {
      shippingAddress: mapNullEntries(account.shippingAddress, {
        country: 'United States',
      }) as AddressFragment,
    }),
  }

  const [storefront, setStorefront] = useState(account.storefront)

  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [isAddressVisible, setIsAddressVisible] = useState(false)
  const [isSavingAddress, setIsSavingAddress] = useState(false)
  const [isChangingSubscription, setIsChangingSubscription] = useState(false)
  const [user, setUser] = useState(initialUser)
  const [isGSAOptInOpen, setIsGSAOptInOpen] = useState(false)
  const [affiliateOptInResponse, setAffiliateOptInResponse] = useState('')
  const [custOptInResponse, setCustOptInResponse] = useState('')
  const [
    isRenewAffiliateConfirmDialogueOpen,
    setIsRenewAffiliateConfirmDialogueOpen,
  ] = useState(false)

  const handleCountryChange = useCallback((country: string) => {
    setUser(prevUser => ({
      ...prevUser,
      shippingAddress: {
        ...prevUser.shippingAddress,
        country,
      },
    }))
  }, [])

  const handleStateChange = useCallback((state: string) => {
    setUser(prevUser => ({
      ...prevUser,
      shippingAddress: {
        ...prevUser.shippingAddress,
        state,
      },
    }))
  }, [])

  const handleShippingAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUser(prevUser => ({
        ...prevUser,
        shippingAddress: {
          ...prevUser.shippingAddress,
          [event.target.name]: event.target.value,
        },
      }))
    },
    [],
  )

  const handleShowAddress = useCallback(() => {
    setIsAddressVisible(prevShowAddress => !prevShowAddress)
  }, [])

  const changeSubscription = useCallback(
    async (subscription?: SubscriptionType) => {
      setIsChangingSubscription(true)

      const sub = subscription
        ? {
            id: subscription.id,
            cancelAtPeriodEnd: false,
          }
        : {
            plan: GS_AFFILIATE_ID,
          }

      try {
        const { updateAccount } = await mutations.updateAccount({
          account: {},
          subscription: sub,
        })

        if (updateAccount) {
          actions.updatedAccount(updateAccount.account)
        }
      } catch (error) {
        console.error(error.toString())
      } finally {
        setIsChangingSubscription(false)
      }
    },
    [actions, mutations],
  )

  const handleUpdateSetting = useCallback(
    async <
      SettingName extends keyof Omit<AccountSettingsInput, 'defaultBackPanelId'>
    >(
      settingName: SettingName,
      value: boolean,
    ) => {
      try {
        const result = await mutations.updateAccount({
          account: { settings: { [settingName]: value } },
        })

        actions.updatedAccount(result.updateAccount.account)
      } catch (error) {
        console.log(error)
      }
    },
    [actions, mutations],
  )

  const handleStorefrontUpdate = useCallback(
    async (storefront: Storefront) => {
      const { mugshot, ...storefrontInput } = storefront

      try {
        const result = await mutations.updateAccount({
          account: { storefront: storefrontInput },
        })

        actions.updatedAccount(result.updateAccount.account)
        setStorefront(result.updateAccount.account.storefront)
      } catch (error) {
        console.error(error)
      }
    },
    [actions, mutations],
  )

  const handleSaveMugshot = useCallback(
    async (file: File) => {
      if (!file) return
      const data = await mutations.updateStorefrontMugshot({
        image_file: file,
        tags: ['prompt-editor'],
      })
      setStorefront(() => ({
        ...data.updateStorefrontMugshot.storefront,
      }))
      actions.updatedAccount({
        ...account,
        storefront: data.updateStorefrontMugshot.storefront,
      })
    },
    [account, actions, mutations],
  )

  const submitAddress = useCallback(async () => {
    setIsSavingAddress(true)

    try {
      await updateAccount(user)
      setIsAddressVisible(false)
    } finally {
      setIsSavingAddress(false)
    }
  }, [updateAccount, user])

  const toggleAlert = useCallback(() => {
    setIsAlertOpen(prevShowAlert => !prevShowAlert)
  }, [])

  const isSRP =
    account &&
    account.subscriptions.filter(
      (sub: SubscriptionType) => sub.sku === SENDOGO_RP_STRIPE_ID,
    ).length >= 1

  const handleGSAOptIn = () => {
    setIsGSAOptInOpen(!isGSAOptInOpen)
  }
  const onStepsComplete = () => {
    // Affiliate Options
    if (affiliateOptInResponse === ActionKeys.optIn) {
      mutations.createAffiliate({
        account: { username: account.username },
        govId1: '',
        verticalId: vertical.graphqlVerticalID,
        queryParams,
      })
    }

    if (affiliateOptInResponse === ActionKeys.optOut) {
      mutations.updateUserLabelOverrides({
        overrides: [
          {
            label: OverridableUserLabel.NEEDS_GS_AFFILIATE_OPT_IN,
            action: UserLabelOverrideAction.EXCLUDE,
          },
        ],
      })
    }
    // Customer options
    if (custOptInResponse === ActionKeys.optIn) {
      mutations.createAffiliate({
        account: { username: account.username },
        govId1: '',
        affiliate: false,
        verticalId: vertical.graphqlVerticalID,
        queryParams,
      })
    }

    if (custOptInResponse === ActionKeys.optOut) {
      mutations.updateUserLabelOverrides({
        overrides: [
          {
            label: OverridableUserLabel.NEEDS_CUSTOMER_REFERRAL_OPT_IN,
            action: UserLabelOverrideAction.EXCLUDE,
          },
        ],
      })
    }
  }
  const isCustomer = account.type === 'C'
  const isDistributor = account.type === 'D'
  const isGSAffiliate = account.type === 'GSA'
  return (
    <div>
      <ConfirmDialog
        isOpen={isRenewAffiliateConfirmDialogueOpen}
        title={'Renew your Affiliate subscription'}
        description={
          'Would you like to purchase an Affiliate subscription renewal?'
        }
        accept={{
          title: 'Confirm renewal',
          onClick: () => {
            changeSubscription()
            setIsRenewAffiliateConfirmDialogueOpen(false)
          },
        }}
        decline={{
          title: 'Cancel',
          onClick: () => {
            setIsRenewAffiliateConfirmDialogueOpen(false)
          },
        }}
        zIndex={9000}
        primaryAction={'accept'}
        onClose={() => setIsRenewAffiliateConfirmDialogueOpen(false)}
      />
      <div className={styles.accountWrapper}>
        <section className={`${styles.column} ${styles.order2}`}>
          <UiCard
            title={'My Address'}
            actions={[
              {
                title: !isAddressVisible ? 'Edit address' : 'Close',
                onClick: handleShowAddress,
              },
            ]}
            buttonColor={vertical.id as ButtonColorChoices}
          >
            <ShippingAddress
              isSavingAddress={isSavingAddress}
              shippingAddress={user.shippingAddress}
              isAddressVisible={isAddressVisible}
              onCountryChange={handleCountryChange}
              onStateChange={handleStateChange}
              onShippingAddressChange={handleShippingAddress}
              onSubmitAddress={submitAddress}
            />
          </UiCard>
          <StreamLinksCard isAffiliate={account.isAffiliate} isSRP={isSRP} />
          <UiCard
            title={'My Subscriptions'}
            actions={[
              ...(account.isAffiliate
                ? [
                    {
                      title: 'GS PRO',
                      id: 'gs_pro_btn',
                      onClick: () => {
                        window.open(account.promptingProLoginUrl, '_blank')
                      },
                    },
                  ]
                : []),
              ...(isSRP
                ? [
                    {
                      title: 'Sendōgo RP',
                      id: 'sendogo_rp_btn',
                      onClick: () => {
                        window.open('https://sendogo.com/srp', '_blank')
                      },
                    },
                  ]
                : []),
              ...(!account.labels.includes(Label.SHOULD_HIDE_REVOLUTION_OFFICE)
                ? [
                    {
                      title: account.isAffiliate
                        ? 'Revolution Office'
                        : 'Invoice History',
                      id: 'revolution_office_btn',
                      onClick: () => {
                        window.open(
                          `${LEGACY_SITE_URL}/bd/backoffice`,
                          '_blank',
                        )
                      },
                    },
                  ]
                : []),
            ]}
            buttonColor={vertical.id as ButtonColorChoices}
          >
            <Subscriptions
              account={accountWithAddons}
              changeSubscription={changeSubscription}
              isChangingSubscription={!!isChangingSubscription}
              setIsConfirmDialogueOpen={setIsRenewAffiliateConfirmDialogueOpen}
            />
          </UiCard>
          {isClientSOC && (
            <EmailPreferences
              settings={account.settings as UserSettingsFragment}
              onToggle={handleUpdateSetting}
            />
          )}
        </section>
        <section className={`${styles.column} ${styles.order1}`}>
          <UiCard
            contentStyle={
              {
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
              } as React.CSSProperties
            }
            buttonColor={vertical.id as ButtonColorChoices}
          >
            <ProfileAvatar
              account={account}
              size={100}
              style={
                {
                  fontSize: '3.7rem',
                  color: '#fff',
                  letterSpacing: 1,
                } as React.CSSProperties
              }
              isEditable={true}
            />
            <Div display="flex">
              <Text type="body" color="primaryHeading">
                {`My ID Number:`}
              </Text>
              <Spacer space="x1" orientation="horizontal" />
              <Text type="body">{account.genealogyId}</Text>
            </Div>
            <UserProfileForm
              shouldSetupShippingAddress={false}
              shouldSetupSponsor={false}
            />
          </UiCard>
          <div style={{ overflowX: 'scroll' }}>
            <SharingInformationCard>
              {account.sponsor && account.sponsor.genealogyId !== 9070 && (
                <SponsorCard sponsor={account.sponsor} />
              )}
              <FlexRow style={{ justifyContent: 'space-around' }}>
                {isCustomer && (
                  <Button
                    title="Join Customer Referral Program"
                    onClick={handleGSAOptIn}
                  />
                )}
                {isDistributor && (
                  <Button
                    title="Join Greener Still as an Affiliate"
                    onClick={handleGSAOptIn}
                  />
                )}
              </FlexRow>
              <Spacer space="x2" />
              {storefront && (account.isAffiliate || isGSAffiliate) && (
                <PersonalSiteOptions
                  account={account}
                  storefront={storefront}
                  onSave={handleStorefrontUpdate}
                  onSaveMugshot={handleSaveMugshot}
                />
              )}
              <div style={{ overflowX: 'auto' }}>
                <VerticalTabs toggleAlert={toggleAlert} />
              </div>
            </SharingInformationCard>
          </div>
        </section>
      </div>
      {isGSAOptInOpen && (
        <Sheet isOpen={true}>
          {isDistributor ? (
            <ProgramOptIn
              message="greenerStillAffiliateOptInMessage"
              onComplete={() => console.log()}
              setResponse={setAffiliateOptInResponse}
            />
          ) : (
            <ProgramOptIn
              message="customerReferralOptInMessage"
              onComplete={() => console.log()}
              setResponse={setCustOptInResponse}
            />
          )}
          <FlexRow css={{ justifyContent: 'space-between', width: 'inherit' }}>
            <Button
              title={'Cancel'}
              type={'secondary'}
              size={'medium'}
              onClick={() => setIsGSAOptInOpen(false)}
            />
            <Button
              title={'Submit'}
              type={'primary'}
              size={'medium'}
              onClick={() => {
                onStepsComplete()
                setIsGSAOptInOpen(false)
              }}
            />
          </FlexRow>
        </Sheet>
      )}
      {isAlertOpen && (
        <Alert
          title=""
          isOpen={isAlertOpen}
          onClose={toggleAlert}
          type={'info'}
          timeout={3000}
          message={'Link copied to clipboard'}
        />
      )}
    </div>
  )
}

export default Account
